import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react';

export interface LLMMarkdownTableMetadata {
  title?: string;
  subtitle?: string;
}

interface LLMMarkdownTableMetadataContextValue {
  setPendingTitle: (title: string) => void;
  setPendingSubtitle: (subtitle: string) => void;
  consumePendingMetadata: () => LLMMarkdownTableMetadata;
}

const LLMMarkdownTableMetadataContext = createContext<LLMMarkdownTableMetadataContextValue | null>(null);

export function LLMMarkdownTableMetadataProvider({children}: {children: React.ReactNode}): JSX.Element {
  const pendingRef = useRef<LLMMarkdownTableMetadata>({});

  const setPendingTitle = useCallback((title: string) => {
    pendingRef.current = {title, subtitle: undefined};
  }, []);

  const setPendingSubtitle = useCallback((subtitle: string) => {
    pendingRef.current = {...pendingRef.current, subtitle};
  }, []);

  const consumePendingMetadata = useCallback((): LLMMarkdownTableMetadata => {
    const metadata = {...pendingRef.current};
    pendingRef.current = {};
    return metadata;
  }, []);

  const value = useMemo(
    () => ({setPendingTitle, setPendingSubtitle, consumePendingMetadata}),
    [setPendingTitle, setPendingSubtitle, consumePendingMetadata]
  );

  return (
    <LLMMarkdownTableMetadataContext.Provider value={value}>
      {children}
    </LLMMarkdownTableMetadataContext.Provider>
  );
}

export function useLLMMarkdownTableMetadata(): LLMMarkdownTableMetadataContextValue {
  const context = useContext(LLMMarkdownTableMetadataContext);
  if (context == null) {
    throw new Error('useLLMMarkdownTableMetadata must be used within LLMMarkdownTableMetadataProvider');
  }
  return context;
}

/** No-op context for TableWithCitations rendered outside LLMMarkdownSection (e.g. unit tests). */
export function useOptionalLLMMarkdownTableMetadata(): LLMMarkdownTableMetadataContextValue | null {
  return useContext(LLMMarkdownTableMetadataContext);
}
